import React, { useState } from 'react';
import {
  LuDatabase,
  LuTable,
  LuKey,
  LuLink,
  LuEye,
  LuFileEdit,
  LuPlus,
  LuTrash2,
  LuRefreshCw,
  LuSearch,
  LuDownload,
  LuSettings,
} from 'react-icons/lu';

const Architecture = ({ node, config, onConfigChange, loading, error, onSave, onDelete, saved }) => {
  const [activeView, setActiveView] = useState('schema');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTable, setSelectedTable] = useState(null);

  // Sample database schema data
  const [schema] = useState({
    tables: [
      {
        name: 'users',
        type: 'table',
        rows: 15420,
        size: '2.3 MB',
        columns: [
          { name: 'id', type: 'SERIAL', nullable: false, primaryKey: true },
          { name: 'email', type: 'VARCHAR(255)', nullable: false, unique: true },
          { name: 'password_hash', type: 'VARCHAR(255)', nullable: false },
          { name: 'first_name', type: 'VARCHAR(100)', nullable: true },
          { name: 'last_name', type: 'VARCHAR(100)', nullable: true },
          { name: 'created_at', type: 'TIMESTAMP', nullable: false, default: 'CURRENT_TIMESTAMP' },
          { name: 'updated_at', type: 'TIMESTAMP', nullable: true }
        ],
        indexes: [
          { name: 'users_email_idx', columns: ['email'], unique: true },
          { name: 'users_created_at_idx', columns: ['created_at'], unique: false }
        ],
        relationships: [
          { table: 'user_profiles', type: 'one-to-one', column: 'user_id' },
          { table: 'orders', type: 'one-to-many', column: 'user_id' }
        ]
      },
      {
        name: 'user_profiles',
        type: 'table',
        rows: 12880,
        size: '1.8 MB',
        columns: [
          { name: 'id', type: 'SERIAL', nullable: false, primaryKey: true },
          { name: 'user_id', type: 'INTEGER', nullable: false, foreignKey: 'users.id' },
          { name: 'bio', type: 'TEXT', nullable: true },
          { name: 'avatar_url', type: 'VARCHAR(500)', nullable: true },
          { name: 'phone', type: 'VARCHAR(20)', nullable: true },
          { name: 'date_of_birth', type: 'DATE', nullable: true },
          { name: 'created_at', type: 'TIMESTAMP', nullable: false, default: 'CURRENT_TIMESTAMP' }
        ],
        indexes: [
          { name: 'user_profiles_user_id_idx', columns: ['user_id'], unique: true }
        ],
        relationships: [
          { table: 'users', type: 'many-to-one', column: 'user_id' }
        ]
      },
      {
        name: 'orders',
        type: 'table',
        rows: 45670,
        size: '6.2 MB',
        columns: [
          { name: 'id', type: 'SERIAL', nullable: false, primaryKey: true },
          { name: 'user_id', type: 'INTEGER', nullable: false, foreignKey: 'users.id' },
          { name: 'total_amount', type: 'DECIMAL(10,2)', nullable: false },
          { name: 'status', type: 'VARCHAR(50)', nullable: false, default: 'pending' },
          { name: 'shipping_address', type: 'TEXT', nullable: true },
          { name: 'created_at', type: 'TIMESTAMP', nullable: false, default: 'CURRENT_TIMESTAMP' },
          { name: 'updated_at', type: 'TIMESTAMP', nullable: true }
        ],
        indexes: [
          { name: 'orders_user_id_idx', columns: ['user_id'], unique: false },
          { name: 'orders_status_idx', columns: ['status'], unique: false },
          { name: 'orders_created_at_idx', columns: ['created_at'], unique: false }
        ],
        relationships: [
          { table: 'users', type: 'many-to-one', column: 'user_id' },
          { table: 'order_items', type: 'one-to-many', column: 'order_id' }
        ]
      }
    ],
    views: [
      {
        name: 'user_order_summary',
        type: 'view',
        definition: 'SELECT u.email, COUNT(o.id) as order_count, SUM(o.total_amount) as total_spent FROM users u LEFT JOIN orders o ON u.id = o.user_id GROUP BY u.id, u.email'
      }
    ],
    functions: [
      {
        name: 'calculate_user_lifetime_value',
        type: 'function',
        returnType: 'DECIMAL(10,2)',
        parameters: ['user_id INTEGER']
      }
    ]
  });

  const getTypeIcon = (type) => {
    switch (type) {
      case 'table':
        return <LuTable className="w-4 h-4" />;
      case 'view':
        return <LuEye className="w-4 h-4" />;
      case 'function':
        return <LuSettings className="w-4 h-4" />;
      default:
        return <LuDatabase className="w-4 h-4" />;
    }
  };

  const getColumnTypeColor = (type) => {
    if (type.includes('VARCHAR') || type.includes('TEXT')) return 'text-green-400';
    if (type.includes('INTEGER') || type.includes('SERIAL') || type.includes('DECIMAL')) return 'text-blue-400';
    if (type.includes('TIMESTAMP') || type.includes('DATE')) return 'text-purple-400';
    if (type.includes('BOOLEAN')) return 'text-yellow-400';
    return 'text-white/80';
  };

  const filteredTables = schema.tables.filter(table =>
    table.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderSchemaView = () => (
    <div className="space-y-4">
      {/* Search and Controls */}
      <div className="flex items-center justify-between">
        <div className="flex-1 relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2">
            <LuSearch className="w-4 h-4 text-white/40" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search tables..."
            className="w-full pl-10 pr-4 py-2 bg-[#ffffff10] border border-[#ffffff20] rounded text-white text-sm placeholder-white/40 focus:outline-none focus:border-[#ffffff40]"
          />
        </div>
        <div className="flex items-center gap-2 ml-4">
          <button className="p-2 bg-[#ffffff10] hover:bg-[#ffffff15] text-white/60 rounded transition-colors" title="Refresh schema">
            <LuRefreshCw className="w-4 h-4" />
          </button>
          <button className="p-2 bg-[#ffffff10] hover:bg-[#ffffff15] text-white/60 rounded transition-colors" title="Export schema">
            <LuDownload className="w-4 h-4" />
          </button>
          <button className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded transition-colors flex items-center gap-2">
            <LuPlus className="w-4 h-4" />
            New Table
          </button>
        </div>
      </div>

      {/* Tables Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filteredTables.map((table) => (
          <div
            key={table.name}
            className="p-4 bg-[#ffffff05] border border-[#ffffff15] rounded-lg hover:bg-[#ffffff08] transition-colors cursor-pointer"
            onClick={() => setSelectedTable(table)}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                {getTypeIcon(table.type)}
                <div>
                  <h4 className="text-white font-medium">{table.name}</h4>
                  <p className="text-white/60 text-sm">{table.rows.toLocaleString()} rows • {table.size}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button className="p-1 hover:bg-[#ffffff10] rounded transition-colors" title="View table">
                  <LuEye className="w-4 h-4 text-white/60" />
                </button>
                <button className="p-1 hover:bg-[#ffffff10] rounded transition-colors" title="Edit table">
                  <LuFileEdit className="w-4 h-4 text-white/60" />
                </button>
                <button className="p-1 hover:bg-[#ffffff10] rounded transition-colors" title="Delete table">
                  <LuTrash2 className="w-4 h-4 text-red-400" />
                </button>
              </div>
            </div>

            {/* Column Preview */}
            <div className="space-y-1">
              <div className="text-white/60 text-xs mb-2">Columns ({table.columns.length})</div>
              {table.columns.slice(0, 4).map((column) => (
                <div key={column.name} className="flex items-center gap-2 text-sm">
                  {column.primaryKey && <LuKey className="w-3 h-3 text-yellow-400" />}
                  {column.foreignKey && <LuLink className="w-3 h-3 text-blue-400" />}
                  <span className="text-white/80">{column.name}</span>
                  <span className={`text-xs ${getColumnTypeColor(column.type)}`}>{column.type}</span>
                </div>
              ))}
              {table.columns.length > 4 && (
                <div className="text-white/60 text-xs">+ {table.columns.length - 4} more columns</div>
              )}
            </div>

            {/* Relationships */}
            {table.relationships.length > 0 && (
              <div className="mt-3 pt-3 border-t border-[#ffffff10]">
                <div className="text-white/60 text-xs mb-1">Relationships</div>
                <div className="flex flex-wrap gap-1">
                  {table.relationships.map((rel, idx) => (
                    <span key={idx} className="text-xs bg-[#ffffff10] text-white/70 px-2 py-1 rounded">
                      {rel.table} ({rel.type})
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Views and Functions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        {/* Views */}
        <div className="p-4 bg-[#ffffff05] border border-[#ffffff15] rounded-lg">
          <h4 className="text-white font-medium mb-3 flex items-center gap-2">
            <LuEye className="w-4 h-4" />
            Views ({schema.views.length})
          </h4>
          <div className="space-y-2">
            {schema.views.map((view) => (
              <div key={view.name} className="p-3 bg-[#ffffff05] border border-[#ffffff10] rounded flex items-center justify-between">
                <span className="text-white/80">{view.name}</span>
                <div className="flex items-center gap-1">
                  <button className="p-1 hover:bg-[#ffffff10] rounded transition-colors" title="View definition">
                    <LuEye className="w-4 h-4 text-white/60" />
                  </button>
                  <button className="p-1 hover:bg-[#ffffff10] rounded transition-colors" title="Edit view">
                    <LuFileEdit className="w-4 h-4 text-white/60" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Functions */}
        <div className="p-4 bg-[#ffffff05] border border-[#ffffff15] rounded-lg">
          <h4 className="text-white font-medium mb-3 flex items-center gap-2">
            <LuSettings className="w-4 h-4" />
            Functions ({schema.functions.length})
          </h4>
          <div className="space-y-2">
            {schema.functions.map((func) => (
              <div key={func.name} className="p-3 bg-[#ffffff05] border border-[#ffffff10] rounded">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-white/80">{func.name}</span>
                  <div className="flex items-center gap-1">
                    <button className="p-1 hover:bg-[#ffffff10] rounded transition-colors" title="View function">
                      <LuEye className="w-4 h-4 text-white/60" />
                    </button>
                    <button className="p-1 hover:bg-[#ffffff10] rounded transition-colors" title="Edit function">
                      <LuFileEdit className="w-4 h-4 text-white/60" />
                    </button>
                  </div>
                </div>
                <div className="text-xs text-white/60">
                  Returns: <span className="text-purple-400">{func.returnType}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderRelationshipsView = () => (
    <div className="space-y-6">
      <div className="text-center py-12">
        <LuLink className="w-16 h-16 text-white/40 mx-auto mb-4" />
        <h4 className="text-white/80 font-medium mb-2">Relationship Diagram</h4>
        <p className="text-white/60 text-sm mb-4">
          Visual representation of table relationships coming soon
        </p>
        <button className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded transition-colors">
          Generate Diagram
        </button>
      </div>
    </div>
  );

  const renderTableDetails = () => {
    if (!selectedTable) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-[#1a1a1a] border border-[#ffffff15] rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="p-6 border-b border-[#ffffff15] flex items-center justify-between">
            <div className="flex items-center gap-3">
              {getTypeIcon(selectedTable.type)}
              <div>
                <h3 className="text-white font-medium text-lg">{selectedTable.name}</h3>
                <p className="text-white/60 text-sm">{selectedTable.rows.toLocaleString()} rows • {selectedTable.size}</p>
              </div>
            </div>
            <button
              onClick={() => setSelectedTable(null)}
              className="p-2 hover:bg-[#ffffff10] rounded transition-colors"
            >
              <LuTrash2 className="w-5 h-5 text-white/60" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
            {/* Columns */}
            <div className="mb-6">
              <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                <LuTable className="w-4 h-4" />
                Columns ({selectedTable.columns.length})
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#ffffff15]">
                      <th className="text-left text-white/60 text-sm py-2">Name</th>
                      <th className="text-left text-white/60 text-sm py-2">Type</th>
                      <th className="text-left text-white/60 text-sm py-2">Nullable</th>
                      <th className="text-left text-white/60 text-sm py-2">Default</th>
                      <th className="text-left text-white/60 text-sm py-2">Constraints</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedTable.columns.map((column) => (
                      <tr key={column.name} className="border-b border-[#ffffff05]">
                        <td className="py-2 text-white/80 flex items-center gap-2">
                          {column.primaryKey && <LuKey className="w-3 h-3 text-yellow-400" title="Primary Key" />}
                          {column.foreignKey && <LuLink className="w-3 h-3 text-blue-400" title="Foreign Key" />}
                          {column.name}
                        </td>
                        <td className={`py-2 text-sm ${getColumnTypeColor(column.type)}`}>{column.type}</td>
                        <td className="py-2 text-sm">
                          <span className={column.nullable ? 'text-green-400' : 'text-red-400'}>
                            {column.nullable ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="py-2 text-white/60 text-sm">{column.default || '-'}</td>
                        <td className="py-2 text-white/60 text-sm">
                          {column.unique && <span className="bg-blue-500/20 text-blue-400 px-2 py-1 rounded text-xs mr-1">UNIQUE</span>}
                          {column.primaryKey && <span className="bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded text-xs mr-1">PK</span>}
                          {column.foreignKey && <span className="bg-purple-500/20 text-purple-400 px-2 py-1 rounded text-xs">FK</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Indexes */}
            <div className="mb-6">
              <h4 className="text-white font-medium mb-3">Indexes ({selectedTable.indexes.length})</h4>
              <div className="space-y-2">
                {selectedTable.indexes.map((index) => (
                  <div key={index.name} className="p-3 bg-[#ffffff05] border border-[#ffffff10] rounded flex items-center justify-between">
                    <div>
                      <span className="text-white/80">{index.name}</span>
                      <div className="text-white/60 text-sm">
                        Columns: {index.columns.join(', ')}
                        {index.unique && <span className="ml-2 bg-blue-500/20 text-blue-400 px-2 py-1 rounded text-xs">UNIQUE</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Relationships */}
            <div>
              <h4 className="text-white font-medium mb-3">Relationships ({selectedTable.relationships.length})</h4>
              <div className="space-y-2">
                {selectedTable.relationships.map((rel, idx) => (
                  <div key={idx} className="p-3 bg-[#ffffff05] border border-[#ffffff10] rounded flex items-center justify-between">
                    <div>
                      <span className="text-white/80">{rel.table}</span>
                      <div className="text-white/60 text-sm">
                        Type: {rel.type} • Column: {rel.column}
                      </div>
                    </div>
                    <LuLink className="w-4 h-4 text-blue-400" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-white font-medium text-lg flex items-center gap-2">
              <LuDatabase className="w-5 h-5" />
              Database Architecture
            </h3>
            <p className="text-white/60 text-sm mt-1">
              Explore your database schema, relationships, and structure
            </p>
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex items-center gap-1 bg-[#ffffff05] p-1 rounded-lg w-fit">
          <button
            onClick={() => setActiveView('schema')}
            className={`px-4 py-2 text-sm rounded transition-colors ${
              activeView === 'schema'
                ? 'bg-blue-500 text-white'
                : 'text-white/60 hover:text-white/80 hover:bg-[#ffffff05]'
            }`}
          >
            Schema
          </button>
          <button
            onClick={() => setActiveView('relationships')}
            className={`px-4 py-2 text-sm rounded transition-colors ${
              activeView === 'relationships'
                ? 'bg-blue-500 text-white'
                : 'text-white/60 hover:text-white/80 hover:bg-[#ffffff05]'
            }`}
          >
            Relationships
          </button>
        </div>
      </div>

      {/* Content */}
      {activeView === 'schema' && renderSchemaView()}
      {activeView === 'relationships' && renderRelationshipsView()}

      {/* Table Details Modal */}
      {renderTableDetails()}
    </div>
  );
};

export default Architecture;